// Angular
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// Libraries
import { BehaviorSubject } from 'rxjs';

// Models
import { JobDb, JobDataIDB, CachedJob, JobDataIDBResponse } from './job-db';
import { JobData } from '../../modules/job/models';
import { UnitPeriodChannelDto } from '../../modules/channel/models/unit-period-channel-dto.model';
import { JobAccessDb } from './job-access-db';
import { JobReturnCache, RunningFootage } from '../../modules/job/models/job-return-cache.model';
import { UnitPeriodDto } from '../../modules/job/models/unit-period-dto.model';
import { JobDataSampleSaturatedModel } from '../../modules/job/models/sample-data-saturation-model';

// Services
import { UtilityService } from '../services/utility.service';
import { AppCleanup } from '../models';

@Injectable({
	providedIn: 'root',
})
export class JobDataService extends AppCleanup {
	private static readonly FOURTYFIVE_MINUTES = 30 * 60 * 1000;
	private static readonly ONE_HOUR = 60 * 60 * 1000;
	private static readonly ONE_DAY = 24 * 60 * 60 * 1000;
	private static readonly SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

	private db: JobDb;
	private jobAccessDb: JobAccessDb;
	private JobLoader;
	private jobLoader;
	cachedJob: CachedJob;
	// cachedJobCollections: CachedJobCollection[] = [];
	private baseUrl: string;
	private token: string;

	protected dataReadyVar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	dataReady$ = this.dataReadyVar.asObservable();

	protected jobDataReadyVar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	jobDataReady$ = this.jobDataReadyVar.asObservable();

	protected resultsChangedVar: BehaviorSubject<JobDataIDBResponse> = new BehaviorSubject<JobDataIDBResponse>(null);
	resultsChanged$ = this.resultsChangedVar.asObservable();

	private jobKey: string;

	constructor(
		private http: HttpClient,
		private utilityService: UtilityService
	) {
		super();
		this.baseUrl = this.apiUrl;
		// this.token = localStorage.hasOwnProperty(environment.authTokenKey)
		// 	? localStorage.getItem(environment.authTokenKey)
		// 	: null;

		this.db = new JobDb();
		this.jobAccessDb = new JobAccessDb();

		// No longer using worker
		// this.JobLoader = Comlink.wrap(new Worker('./job-loader.worker', {type: 'module'}));
	}

	getJobDataSample(jobKey: string, unitPeriodId: number, startDate: number, endDate?: number, secondSpan?: number, page?: number, pageSize?: number) {
		if (secondSpan >= 0 && page >= 0 && pageSize >= 0) {
			if (!endDate) {
				return this.http.get<JobReturnCache>(`${this.baseUrl}api/job/sample/${jobKey}/${unitPeriodId}?startDate=${startDate}&secondSpan=${secondSpan}&page=${page}&pageSize=${pageSize}`);
			} else {
				return this.http.get<JobReturnCache>(`${this.baseUrl}api/job/sample/${jobKey}/${unitPeriodId}?startDate=${startDate}&endDate=${endDate}&secondSpan=${secondSpan}&page=${page}&pageSize=${pageSize}`);
			}
		}
		if (!endDate) {
			return this.http.get<JobReturnCache>(`${this.baseUrl}api/job/sample/${jobKey}/${unitPeriodId}?startDate=${startDate}`);
		} else {
			return this.http.get<JobReturnCache>(`${this.baseUrl}api/job/sample/${jobKey}/${unitPeriodId}?startDate=${startDate}&endDate=${endDate}`);
		}
	}

	getJobDataSampleSaturated(model: JobDataSampleSaturatedModel) {
		const { jobKey, unitPeriodId } = model;
		const params: string = this.utilityService.buildUrlParams(model);
		const url: string = `${this.baseUrl}api/job/sample/${jobKey}/${unitPeriodId}?${params}`;

		return this.http.get<JobReturnCache>(url);
	}

	getRunningFootage(jobKey: string) {
		return this.http.get<RunningFootage>(`${this.baseUrl}api/job/running-footage/${jobKey}`);
	}

	downloadJobData(jobKey: string, unitPeriodId: number, startDate: number, endDate?: number) {
		if (!endDate) {
			return this.http.get<JobReturnCache>(`${this.baseUrl}api/job/sample/download/${jobKey}/${unitPeriodId}?startDate=${startDate}`);
		} else {
			return this.http.get<JobReturnCache>(`${this.baseUrl}api/job/sample/download/${jobKey}/${unitPeriodId}?startDate=${startDate}&endDate=${endDate}`);
		}
	}

	getPublicJobDataSample(jobKey: string, unitPeriodId: number, startDate: number, endDate?: number, secondSpan?: number, page?: number, pageSize?: number) {
		if (secondSpan >= 0 && page >= 0 && pageSize >= 0) {
			if (!endDate) {
				return this.http.get<JobReturnCache>(`${this.baseUrl}api/job/public/sample/${jobKey}/${unitPeriodId}?startDate=${startDate}&secondSpan=${secondSpan}&page=${page}&pageSize=${pageSize}`);
			} else {
				return this.http.get<JobReturnCache>(`${this.baseUrl}api/job/public/sample/${jobKey}/${unitPeriodId}?startDate=${startDate}&endDate=${endDate}&secondSpan=${secondSpan}&page=${page}&pageSize=${pageSize}`);
			}
		}
		if (!endDate) {
			return this.http.get<JobReturnCache>(`${this.baseUrl}api/job/public/sample/${jobKey}/${unitPeriodId}?startDate=${startDate}`);
		} else {
			return this.http.get<JobReturnCache>(`${this.baseUrl}api/job/public/sample/${jobKey}/${unitPeriodId}?startDate=${startDate}&endDate=${endDate}`);
		}
	}

	/** Initialize the Job Loader WebWorker to sync up IndexedDB with new data from the Database.
	 * @param syncTime Sets the time at which the service should check for new data from the DB and sync with IndexedDB
	 */
	// async initProvider(syncTime: number) {

	//     this.jobLoader = await new this.JobLoader();
	//     this.initData();

	//     if (!this.isViewer && syncTime) {
	//       if(this.token) {
	//           setInterval(() => {
	//             this.syncData();
	//           }, syncTime);
	//       }
	//     } else {
	//       this.syncData();
	//     }

	// }

	/** Sends message to WebWorker to set the JWT, set the API URL, and then finds new active job data and updates IndexedDB */
	// initData() {
	//   if(this.isViewer || this.token) {
	//     this.jobLoader.initData(this.token, this.baseUrl).then(() => {
	//       this.dataReadyVar.next(true);
	//     });
	//   } else {
	//     this.dataReadyVar.next(true);
	//   }
	// }

	// async initJobData(jobId: number, jobStatusId: number) {

	//   if(!this.jobLoader) {
	//     this.jobLoader = await new this.JobLoader('abc', 'abc');
	//     this.jobLoader.initTokenAndUrl(this.token, this.baseUrl);
	//   }

	//   this.jobLoader.updateJobWithLatestData(jobId, jobStatusId).then(() => {
	//     this.fetchJob(jobId).then(() => {
	//       this.jobDataReadyVar.next(true);
	//     });
	//   });

	// }

	/** Sends message to Web Worker to find new active job data and update IndexedDB */
	// syncData() {

	//   this.jobLoader.syncData().then(() => {
	//     this.dataReadyVar.next(true);
	//   });

	// }

	/**Retrieve all data from IndexedDB for Jobs */
	// async fetchAll(): Promise<JobDataIDB[]> {
	//   return this.db.jobs.toArray();
	// }

	/**Retrieve job data from IndexedDB and cache job in service. Also returns job info of cached data.
	 * @param pJobId Job ID for Job to find and Cache
	 */
	// async fetchJob(pJobId: number): Promise<JobDataIDB[]> {

	//   if(!this.jobLoader) {
	//     this.jobLoader = await new this.JobLoader('abc', 'abc');;
	//   }

	//   let job = await this.db.jobs.where('jobId').equals(pJobId).toArray();

	//   this.cachedJob = {
	//     jobId: String(pJobId),
	//     jobData: job
	//   };

	//   return job;
	// }

	/**Finds Job cached in memory and filters data based on payload parameter
	 * @param pJobDataPayload Parameter object that stores all values necessary to filter appropriately
	 */
	// getCachedJobAndFilter(pJobDataPayload: GetJobDataPayload, jobChannels: JobChannelDto[], jobChannelOffsets: JobChannelOffsetDto[]) {

	//   let pointResolution = 1000;

	//   let cachedJob = this.cachedJob;

	//   //For each shift type, shift half as much as the chart interval
	//   let offset: number = 0;
	//   if(pJobDataPayload.shift == ShiftType.LEFT) {
	//     offset = -pJobDataPayload.minutes*30000;
	//   } else if (pJobDataPayload.shift == ShiftType.RIGHT) {
	//     offset = pJobDataPayload.minutes*30000;
	//   } else if (pJobDataPayload.shift == ShiftType.BIGLEFT) {
	//     offset = -pJobDataPayload.minutes*60000;
	//   } else if (pJobDataPayload.shift == ShiftType.RIGHTRIGHT) {
	//     offset = pJobDataPayload.minutes*60000;
	//   }

	//   let result: JobDataIDBResponse = new JobDataIDBResponse();

	//   if(cachedJob.jobData.length > 0) {

	//     let startDate: number = 0;
	//     let endDate: number = 0;

	//     let firstDataPoint = cachedJob.jobData[0];
	//     let lastDataPoint = cachedJob.jobData[cachedJob.jobData.length - 1];

	//     if(pJobDataPayload.minutes === null) {
	//       startDate = pJobDataPayload.startDate.getTime();
	//       endDate = pJobDataPayload.endDate.getTime();
	//     } else if (pJobDataPayload.minutes === 0) {
	//       startDate = firstDataPoint.dateCollected;
	//       endDate = lastDataPoint.dateCollected;
	//     } else if (offset) {
	//         startDate = pJobDataPayload.startDate ? pJobDataPayload.startDate.getTime() + offset : firstDataPoint.dateCollected;
	//         endDate = startDate + pJobDataPayload.minutes*60000;
	//     } else if (pJobDataPayload.minutes > 0) {
	//       endDate = pJobDataPayload.endDate ? pJobDataPayload.endDate.getTime() : lastDataPoint.dateCollected;
	//       startDate = endDate - pJobDataPayload.minutes*60000;
	//     }

	//     if((lastDataPoint.dateCollected >= startDate && lastDataPoint.dateCollected <= endDate) || lastDataPoint.dateCollected <= startDate) {
	//       result.rightShiftEnded = true;
	//       endDate = lastDataPoint.dateCollected;
	//       startDate = pJobDataPayload.minutes == 0 ? firstDataPoint.dateCollected : lastDataPoint.dateCollected - pJobDataPayload.minutes*60000;

	//       if(firstDataPoint.dateCollected >= startDate && firstDataPoint.dateCollected <= endDate){
	//         result.leftShiftEnded = true;
	//       }

	//     } else if((firstDataPoint.dateCollected >= startDate && firstDataPoint.dateCollected <= endDate) || firstDataPoint.dateCollected >= endDate) {
	//       result.leftShiftEnded = true;
	//       startDate = firstDataPoint.dateCollected;
	//       endDate = pJobDataPayload.minutes == 0 ? lastDataPoint.dateCollected : firstDataPoint.dateCollected + pJobDataPayload.minutes*60000;
	//     }

	//     let timeSpan: number = (endDate - startDate) / pointResolution;
	//     let dataPoints: JobDataMappingModel[] = [];

	//     let initialPointDate: number = null;
	//     for(let i = cachedJob.jobData.length - 1; i >= 0; i--) {

	//       let jobDataPoint = cachedJob.jobData[i];

	//       if(initialPointDate && jobDataPoint.dateCollected < (initialPointDate - timeSpan)) {

	//         let convertedData: JobDataPoint[] = [];

	//         dataPoints.forEach((dps) => {
	//           convertedData.push({
	//             channelId: dps.channelId,
	//             value: dps.totalPoints > 0 ? dps.totalValue / dps.totalPoints : null
	//           });
	//         });

	//         let jobData: JobDataIDB = {
	//           id: 0,
	//           jobId: jobDataPoint.jobId,
	//           dateCollected: (initialPointDate + jobDataPoint.dateCollected) / 2,
	//           data: convertedData
	//         }

	//         result.jobData.unshift(jobData);

	//         dataPoints = [];

	//         initialPointDate = jobDataPoint.dateCollected;
	//       }

	//       if(jobDataPoint.dateCollected > endDate) {
	//         continue;
	//       } else if (jobDataPoint.dateCollected < startDate) {
	//         break;
	//       }else if((i < cachedJob.jobData.length - 1 && jobDataPoint.dateCollected === cachedJob.jobData[i + 1].dateCollected)) {
	//         //Removing duplicate points
	//         continue;
	//       }

	//       if (!initialPointDate) {
	//         initialPointDate = jobDataPoint.dateCollected;
	//       }
	//       //If we made it here, we are in range
	//       jobDataPoint.data.forEach(d => {

	//         let existingPoint = dataPoints.find(dp => dp.channelId === d.channelId);

	//         if (existingPoint) {
	//           existingPoint.totalValue += d.value;
	//           existingPoint.totalPoints++;
	//         } else {
	//           dataPoints.push({
	//             channelId: d.channelId,
	//             totalValue: d.value,
	//             totalPoints: 1
	//           })
	//         }
	//       });

	//       //result.jobData.unshift(cachedJob.jobData[i]);

	//     }

	//     //Adding one null point so the date axis draws correctly
	//     if(result.jobData.length == 0) {
	//       result.jobData.push({
	//         id: 0,
	//         jobId: 0,
	//         data: [],
	//         dateCollected: startDate
	//       })
	//     }

	//     //Handling offsets
	//     if (jobChannelOffsets && jobChannelOffsets.length > 0) {
	//       for (var i = jobChannelOffsets.length - 1; i >= 0; i--) {
	//         let jobChannelOffset = jobChannelOffsets[i];

	//         let impactedPoints;

	//         if (i === jobChannelOffsets.length - 1) {
	//           impactedPoints = result.jobData.filter(jd => jd.dateCollected >= jobChannelOffset.activatedDateUtc.getTime());
	//         } else {
	//           let jobChannelOffsetPrev: JobChannelOffsetDto = jobChannelOffsets[i + 1];
	//           impactedPoints = result.jobData.filter(jd => jd.dateCollected >= jobChannelOffset.activatedDateUtc.getTime() &&
	//                                                        jd.dateCollected < jobChannelOffsetPrev.activatedDateUtc.getTime());
	//         }

	//         impactedPoints.forEach(ip => {
	//           ip.data.forEach(d => {
	//             if(d.channelId === jobChannelOffset.channelId) {
	//               d.value += jobChannelOffset.offsetValue;
	//             }
	//           });
	//         })
	//       }
	//       // jobChannelOffsets.forEach(jco => {
	//       //   let impactedPoints = result.jobData.filter(jd => jd.dateCollected )
	//       // });

	//     }

	//     result.jobData.forEach(jd => {
	//       jd.data.forEach(d => {
	//         if (d.value !== null) {
	//           let precision = jobChannels.find(jc => jc.channelId == d.channelId)?.channel?.precision;
	//           d.value = +d.value.toFixed(precision);
	//         }
	//       });
	//     });

	//     result.startDate = startDate;
	//     result.endDate = endDate;

	//   }

	//   this.resultsChangedVar.next(result);

	// }

	applyOffsets(unitPeriod: UnitPeriodDto, jobChannels: UnitPeriodChannelDto[]) {
		// if (unitPeriod.unitPeriodChannels && unitPeriod.unitPeriodChannels.length > 0) {
		//   for (var i = unitPeriod.jobChannelOffsets.length - 1; i >= 0; i--) {
		//     let jobChannelOffset = job.jobChannelOffsets[i];
		//     let impactedPoints
		//     if (i === job.jobChannelOffsets.length - 1) {
		//       impactedPoints = job.data.filter(jd => jd.date.getTime() >= jobChannelOffset.activatedDateUtc.getTime());
		//     } else {
		//       let jobChannelOffsetPrev: JobChannelOffsetDto = job.jobChannelOffsets[i + 1];
		//       impactedPoints = job.data.filter(jd => jd.date.getTime() >= jobChannelOffset.activatedDateUtc.getTime() &&
		//                                              jd.date.getTime() < jobChannelOffsetPrev.activatedDateUtc.getTime());
		//     }
		//     impactedPoints.forEach(ip => {
		//       ip.channels.forEach(d => {
		//         if(d.channelId === jobChannelOffset.channelId) {
		//           d.value += jobChannelOffset.offsetValue;
		//         }
		//       });
		//     })
		//   }
		//   // jobChannelOffsets.forEach(jco => {
		//   //   let impactedPoints = result.jobData.filter(jd => jd.dateCollected )
		//   // });
		// }
		// job.data.forEach(d => {
		//   d.channels.forEach(c => {
		//     if (c.value !== null) {
		//       let precision = jobChannels.find(jc => jc.channelId == c.channelId)?.channel?.precision;
		//       c.value = +c.value.toFixed(precision);
		//     }
		//   });
		// });
	}

	// applyOffsetsForJobDataResponse(jobDataResponse: JobDataResponse, jobChannelOffsets: UnitPeriodChannelOffsetDto[], jobChannels: UnitPeriodChannelDto[]) {
	//   if (jobChannelOffsets && jobChannelOffsets.length > 0) {
	//     for (var i = jobChannelOffsets.length - 1; i >= 0; i--) {
	//       let jobChannelOffset = jobChannelOffsets[i];

	//       let impactedPoints;
	//       if (i === jobChannelOffsets.length - 1) {
	//         impactedPoints = jobDataResponse.data.filter(jd => jd.date.getTime() >= jobChannelOffset.activatedDateUtc.getTime());
	//       } else {
	//         let jobChannelOffsetPrev: UnitPeriodChannelOffsetDto = jobChannelOffsets[i + 1];
	//         impactedPoints = jobDataResponse.data.filter(jd => jd.date.getTime() >= jobChannelOffset.activatedDateUtc.getTime() &&
	//                                                            jd.date.getTime() < jobChannelOffsetPrev.activatedDateUtc.getTime());
	//       }

	//       impactedPoints.forEach(ip => {
	//         ip.channels.forEach(d => {
	//           if(d.channelId === jobChannelOffset.channelId) {
	//             let precision = jobChannels.find(jc => jc.channelId == d.channelId)?.channel?.precision;
	//             d.value += jobChannelOffset.offsetValue;
	//             d.value = +d.value.toFixed(precision);
	//           }
	//         });
	//       })
	//     }

	//     // jobChannelOffsets.forEach(jco => {
	//     //   let impactedPoints = result.jobData.filter(jd => jd.dateCollected )
	//     // });
	//   }

	//   jobDataResponse.data.forEach(d => {
	//     d.channels.forEach(c => {
	//       if (c.value !== null) {
	//         let precision = jobChannels.find(jc => jc.channelId == c.channelId)?.channel?.precision;
	//         c.value = +c.value.toFixed(precision);
	//       }
	//     });
	//   })

	// }

	// applyOffsetsToAdjustedData(adjustedData: any[], jobChannelOffsets: JobChannelOffsetDto[], jobChannels: JobChannelDto[]) {
	//   if (jobChannelOffsets && jobChannelOffsets.length > 0) {
	//     for (var i = jobChannelOffsets.length - 1; i >= 0; i--) {
	//       let jobChannelOffset = jobChannelOffsets[i];

	//       let impactedPoints;
	//       if (i === jobChannelOffsets.length - 1) {
	//         impactedPoints = adjustedData.filter(jd => jd.date.getTime() >= jobChannelOffset.activatedDateUtc.getTime());
	//       } else {
	//         let jobChannelOffsetPrev: JobChannelOffsetDto = jobChannelOffsets[i + 1];
	//         impactedPoints = adjustedData.filter(jd => jd.date.getTime() >= jobChannelOffset.activatedDateUtc.getTime() &&
	//                                                   jd.date.getTime() < jobChannelOffsetPrev.activatedDateUtc.getTime());
	//       }

	//       impactedPoints.forEach(ip => {
	//         if(Object.prototype.hasOwnProperty.call(ip, jobChannelOffset.channelId)) {
	//           ip[jobChannelOffset.channelId] += jobChannelOffset.offsetValue;
	//         }
	//       });
	//     }

	//     // Add Precision Here. The += is adding more decimals

	//     // jobChannelOffsets.forEach(jco => {
	//     //   let impactedPoints = result.jobData.filter(jd => jd.dateCollected )
	//     // });
	//   }

	//   adjustedData.forEach(d => {
	//     for (var prop in d) {
	//       if (!isNaN(parseInt(prop))) {
	//         let precision = jobChannels.find(jc => jc.channelId == parseInt(prop))?.channel?.precision;
	//         d[prop] = +d[prop].toFixed(precision);
	//       }
	//     }
	//   });

	// }

	addPointsToCache(pJobId: number, data: Array<JobData>) {
		if (this.cachedJob) {
			let jobIdbResults: JobDataIDB[] = [];

			data.forEach((d) => {
				jobIdbResults.push({
					id: 0,
					jobId: pJobId,
					dateCollected: d.d.getTime(),
					data: d.c,
				});
			});

			this.cachedJob.jobData.push(...jobIdbResults);
		}
	}

	// async getInactiveJob(pJobId: string) {
	//   if(!this.jobLoader) {
	//     this.jobLoader = await new this.JobLoader('abc', 'abc');;
	//   }

	//   let job = await this.jobLoader.getJobData(pJobId);

	//   this.cachedJob = {
	//     jobId: pJobId,
	//     jobData: job
	//   };

	//   return job;
	// }

	// async getInactiveJobNew(pJobId: number) {

	//   if(!this.jobLoader) {
	//     this.jobLoader = await new this.JobLoader('abc', 'abc');;
	//   }

	//   let job = await this.jobLoader.getJobData(pJobId);

	//   this.separateJobData(job);
	// }
}
